import { createFileRoute } from '@tanstack/react-router'
import { observer } from 'mobx-react-lite'
import useView from '@/hooks/useView.js'
import { useCallback, useEffect, useRef, useState } from 'react'
import { LoadingIndicator, LoadingIndicatorTitle } from '@/components/ui/loadingIndicator.jsx'
import { Virtuoso } from 'react-virtuoso'
import UserProfile from '@/components/user/userProfile.jsx'
import useStore from '@/hooks/useStore.js'

const List = observer(({ children, ...props }) => (
  <div {...props} className="mx-auto flex max-w-[1600px] flex-col justify-center gap-3 pb-6!">
    {children}
  </div>
))

const Item = observer(({ children, ...props }) => (
  <div
    {...props}
    className="grid grid-cols-1 place-items-center gap-6 md:grid-cols-2 md:gap-3 xl:grid-cols-4">
    {children}
  </div>
))

const UserProfileItem = observer(({ rowIndex, columnIndex, scrollDirection }) => {
  const { favoritesView } = useView()

  const index = rowIndex * 3 + columnIndex
  if (index >= favoritesView.favoriteUsers.length) return null

  return (
    <UserProfile
      className="animate-in data-[scroll-direction=down]:slide-in-from-bottom-3 data-[scroll-direction=up]:slide-in-from-top-3 h-full min-h-[568px] w-full duration-300 will-change-transform md:animate-none"
      data-scroll-direction={scrollDirection}
      user={favoritesView.favoriteUsers[index]}
    />
  )
})

const FavoritesPage = () => {
  const { responsiveStore } = useStore()
  const { favoritesView } = useView()
  const [scrollDirection, setScrollDirection] = useState('down')
  const scrollYRef = useRef(window.scrollY)

  useEffect(() => {
    favoritesView.init().then()

    return () => {
      favoritesView.reset()
    }
  }, [favoritesView])

  const virtuosoItemContent = useCallback(
    i => (
      <>
        <UserProfileItem columnIndex={0} rowIndex={i} scrollDirection={scrollDirection} />
        <UserProfileItem columnIndex={1} rowIndex={i} scrollDirection={scrollDirection} />
        <UserProfileItem columnIndex={2} rowIndex={i} scrollDirection={scrollDirection} />
        <UserProfileItem columnIndex={3} rowIndex={i} scrollDirection={scrollDirection} />
      </>
    ),
    [scrollDirection]
  )

  useEffect(() => {
    let ticking = false

    const updateDirection = () => {
      if (ticking || responsiveStore.isMd) return

      ticking = true
      requestAnimationFrame(() => {
        const next = window.scrollY > scrollYRef.current ? 'down' : 'up'
        if (next !== scrollDirection) setScrollDirection(next)

        scrollYRef.current = window.scrollY
        ticking = false
      })
    }

    window.addEventListener('scroll', updateDirection, { passive: true })

    return () => window.removeEventListener('scroll', updateDirection)
  }, [responsiveStore.isMd, scrollDirection])

  if (!favoritesView.isInitialized)
    return (
      <LoadingIndicator>
        <LoadingIndicatorTitle>Loading...</LoadingIndicatorTitle>
      </LoadingIndicator>
    )

  return (
    <Virtuoso
      className="mx-auto w-full"
      components={{ Item: Item, List: List }}
      itemContent={virtuosoItemContent}
      totalCount={Math.ceil(favoritesView.favoriteUsers.length / 4)}
      useWindowScroll
    />
  )
}

export const Route = createFileRoute('/_authenticated/favorites')({
  component: observer(FavoritesPage),
  staticData: {
    seo: {
      title: 'Favorites',
    },
  },
})
